import { z } from "zod";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const jobApplicationFormSchema = z.object({
  applicant_name: z
    .string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  applicant_email: z
    .string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  applicant_phone: z
    .string()
    .trim()
    .max(20, "Phone number must be less than 20 characters")
    .optional()
    .or(z.literal("")),
  linkedin_url: z
    .string()
    .trim()
    .refine(
      (val) => {
        if (!val) return true;
        // Accept URLs with or without protocol
        const urlPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]+\/?$/i;
        return urlPattern.test(val);
      },
      "Please enter a valid LinkedIn profile URL (e.g., www.linkedin.com/in/yourname)"
    )
    .transform((val) => {
      if (!val) return "";
      // Add https:// if missing
      return val.startsWith("http") ? val : `https://${val}`;
    })
    .optional()
    .or(z.literal("")),
  cover_letter: z
    .string()
    .trim()
    .min(50, "Cover letter must be at least 50 characters")
    .max(2000, "Cover letter must be less than 2000 characters"),
  cv_file: z
    .instanceof(File)
    .refine((file) => file.size <= MAX_FILE_SIZE, "CV must be less than 10MB")
    .refine(
      (file) => ACCEPTED_FILE_TYPES.includes(file.type),
      "CV must be a PDF, DOC, or DOCX file"
    ),
});

export type JobApplicationFormData = z.infer<typeof jobApplicationFormSchema>;
